import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';

import { PageTitle, PrimaryButton, VerticalContainer } from '@american-autonomy/rantizo-ui';

import CompanyDetailsEditCard from 'components/cards/CompanyDetailsEditCard';
import OrganizationCertificationEditCard from 'components/cards/OrganizationCertificationEditCard';
import PersonOfContactEditCard from 'components/cards/PersonOfContactEditCard';

import useCurrentOrganization from 'hooks/useCurrentOrganization';
import usePageRoutes from 'hooks/usePageRoutes';
import useRoles from 'hooks/useRoles';

import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const OrganizationDetails: FunctionComponent<Props> = ({ testId = TEST_ID }) => {
    const { canManageOrganizations } = useRoles();
    const { currentOrganization, refreshCurrentOrganization } = useCurrentOrganization();
    const { accountLoginPage } = usePageRoutes();
    const { BUTTON, TITLE } = useTranslation();

    // Persisted org goes stale on any out-of-session change (cert edits elsewhere,
    // webhook subscription upgrades, admin edits). Refresh on mount; keep the
    // cached copy on transient failure.
    useEffect(() => {
        const abortController = new AbortController();

        void refreshCurrentOrganization(abortController.signal);

        return () => abortController.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (currentOrganization === undefined) {
        return <Navigate data-testid={testId} replace to={accountLoginPage} />;
    }

    const canEditDetails = canManageOrganizations;

    return (
        <VerticalContainer className={styles.pageContainer} testId={testId}>
            <PageTitle className={styles.header} text={TITLE} />

            <div className={styles.cardContainer}>
                <CompanyDetailsEditCard editable={canEditDetails ? canEditDetails : false} />

                <PersonOfContactEditCard editable={canEditDetails ? canEditDetails : false} />

                <OrganizationCertificationEditCard
                    editable={canEditDetails ? canEditDetails : false}
                    organization={currentOrganization}
                />
            </div>

            <PrimaryButton className={styles.transferOrgButton} isDisabled text={BUTTON} />
        </VerticalContainer>
    );
};

export default OrganizationDetails;
